import React, { useState, useEffect } from 'react';
import { XMarkIcon, BookmarkIcon, Cog6ToothIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { deckService } from '../services/deckService';

export interface SpacedRepetitionSettings {
  enabled: boolean;
  intervals: {
    again: number;
    hard: number;
    good: number;
    easy: number;
  };
  multipliers: {
    hard: number;
    good: number;
    easy: number;
  };
}

export const DEFAULT_SPACED_REPETITION_SETTINGS: SpacedRepetitionSettings = {
  enabled: true,
  intervals: {
    again: 1,
    hard: 2,
    good: 3,
    easy: 4,
  },
  multipliers: {
    hard: 1.2,
    good: 1.5,
    easy: 2.5,
  }
};

interface StudyConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartStudy: (cardLimit: number) => void;
  deckName: string;
  availableCards: number;
  deckId: string;
}

const StudyConfigModal: React.FC<StudyConfigModalProps> = ({
  isOpen,
  onClose,
  onStartStudy,
  deckName,
  availableCards,
  deckId
}) => {
  const [cardLimit, setCardLimit] = useState(20);
  const [savedConfig, setSavedConfig] = useState<number | null>(null);
  const [initialCardLimit, setInitialCardLimit] = useState(20);
  const [spacedRepetitionSettings, setSpacedRepetitionSettings] = useState<SpacedRepetitionSettings>(DEFAULT_SPACED_REPETITION_SETTINGS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load saved configuration when modal opens
  useEffect(() => {
    if (isOpen && deckId) {
      // Load deck data including spaced repetition settings
      deckService.getDeck(deckId).then((deck: any) => {
        const savedLimit = deck.studyCardLimit || 20;
        setInitialCardLimit(savedLimit);
        setSavedConfig(savedLimit);
        setCardLimit(savedLimit);
        
        // Load spaced repetition settings from deck
        const deckSettings: SpacedRepetitionSettings = {
          enabled: deck.spacedRepetitionEnabled !== undefined ? deck.spacedRepetitionEnabled : DEFAULT_SPACED_REPETITION_SETTINGS.enabled,
          intervals: {
            again: deck.spacedRepetitionIntervals?.again || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.again,
            hard: deck.spacedRepetitionIntervals?.hard || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.hard,
            good: deck.spacedRepetitionIntervals?.good || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.good,
            easy: deck.spacedRepetitionIntervals?.easy || DEFAULT_SPACED_REPETITION_SETTINGS.intervals.easy,
          },
          multipliers: {
            hard: deck.spacedRepetitionMultipliers?.hard || DEFAULT_SPACED_REPETITION_SETTINGS.multipliers.hard,
            good: deck.spacedRepetitionMultipliers?.good || DEFAULT_SPACED_REPETITION_SETTINGS.multipliers.good,
            easy: deck.spacedRepetitionMultipliers?.easy || DEFAULT_SPACED_REPETITION_SETTINGS.multipliers.easy,
          }
        };
        setSpacedRepetitionSettings(deckSettings);
      }).catch((error) => {
        console.error('Failed to load deck configuration:', error);
      });
    }
  }, [isOpen, deckId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onStartStudy(cardLimit);
    onClose();
  };

  const handleSaveConfiguration = async () => {
    setIsSaving(true);
    try {
      // Save deck config and spaced repetition settings to the deck
      await deckService.updateDeck(deckId, { 
        studyCardLimit: cardLimit,
        spacedRepetitionEnabled: spacedRepetitionSettings.enabled,
        spacedRepetitionIntervals: spacedRepetitionSettings.intervals,
        spacedRepetitionMultipliers: spacedRepetitionSettings.multipliers
      } as any);
      setSavedConfig(cardLimit);
      setInitialCardLimit(cardLimit);
      toast.success('Configuração salva com sucesso!');
    } catch (error) {
      console.error('Failed to save configuration:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setIsSaving(false);
    }
  };

  const updateInterval = (key: keyof SpacedRepetitionSettings['intervals'], value: number) => {
    const newValue = Math.max(1, value);
    
    setSpacedRepetitionSettings(prev => {
      const newIntervals = { ...prev.intervals, [key]: newValue };
      
      // Enforce ascending order: again < hard < good < easy
      if (key === 'again' && newValue >= newIntervals.hard) {
        newIntervals.hard = newValue + 1;
        newIntervals.good = newValue + 2;
        newIntervals.easy = newValue + 3;
      } else if (key === 'hard') {
        if (newValue <= newIntervals.again) {
          newIntervals.hard = newIntervals.again + 1;
        }
        if (newValue >= newIntervals.good) {
          newIntervals.good = newValue + 1;
          newIntervals.easy = newValue + 2;
        }
      } else if (key === 'good') {
        if (newValue <= newIntervals.hard) {
          newIntervals.good = newIntervals.hard + 1;
        }
        if (newValue >= newIntervals.easy) {
          newIntervals.easy = newValue + 1;
        }
      } else if (key === 'easy' && newValue <= newIntervals.good) {
        newIntervals.easy = newIntervals.good + 1;
      }
      
      return {
        ...prev,
        intervals: newIntervals
      };
    });
  };

  const updateMultiplier = (key: keyof SpacedRepetitionSettings['multipliers'], value: number) => {
    setSpacedRepetitionSettings(prev => ({
      ...prev,
      multipliers: {
        ...prev.multipliers,
        [key]: Math.max(1, value),
      }
    }));
  };

  const handleResetSpacedRepetition = () => {
    setSpacedRepetitionSettings(DEFAULT_SPACED_REPETITION_SETTINGS);
  };

  const presetOptions = [10, 20, 30, 50, 100];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" 
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200/50 dark:border-gray-700/50">
            <h2 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Configurar Estudo
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Fechar"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          
          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 max-h-[80vh] overflow-y-auto">
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Configurar sessão de estudo para <span className="font-medium text-gray-900 dark:text-white">{deckName}</span>
              </p>
            </div>

            {/* Spaced Repetition Settings */}
            <div className="mb-6">
              {/* Enable/Disable Toggle */}
              <div className="mb-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Usar Repetição Espaçada
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {spacedRepetitionSettings.enabled 
                        ? 'Os intervalos de revisão serão calculados automaticamente com base no seu desempenho.'
                        : 'Modo simples: todos os cards estarão disponíveis para estudo.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSpacedRepetitionSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      spacedRepetitionSettings.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        spacedRepetitionSettings.enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Configuration Settings - Only show when enabled */}
              {spacedRepetitionSettings.enabled && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <Cog6ToothIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Configurações de Repetição Espaçada
                    </h3>
                  </div>

                  {/* Info Box */}
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <div className="flex gap-2">
                      <InformationCircleIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h5 className="text-xs font-medium text-blue-900 dark:text-blue-200">
                          Como funciona?
                        </h5>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                          Cards marcados como "Perfeito" terão intervalos maiores, enquanto "Novamente" reinicia o ciclo.
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          <strong>Nota:</strong> Os intervalos são ajustados automaticamente para manter a ordem: Novamente &lt; Difícil &lt; Bom &lt; Fácil.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Initial Intervals */}
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-gray-900 dark:text-white mb-3">
                      Intervalos Iniciais (primeira revisão)
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Novamente */}
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                        <label className="block text-xs font-medium text-red-700 dark:text-red-300 mb-1">
                          Novamente
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="30"
                            value={spacedRepetitionSettings.intervals.again}
                            onChange={(e) => updateInterval('again', parseInt(e.target.value) || 1)}
                            className="w-full px-2 py-1.5 text-sm border border-red-200 dark:border-red-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          />
                          <span className="text-xs text-red-600 dark:text-red-400 whitespace-nowrap">dia(s)</span>
                        </div>
                      </div>

                      {/* Difícil */}
                      <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <label className="block text-xs font-medium text-yellow-700 dark:text-yellow-300 mb-1">
                          Difícil
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="60"
                            value={spacedRepetitionSettings.intervals.hard}
                            onChange={(e) => updateInterval('hard', parseInt(e.target.value) || 1)}
                            className="w-full px-2 py-1.5 text-sm border border-yellow-200 dark:border-yellow-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          />
                          <span className="text-xs text-yellow-600 dark:text-yellow-400 whitespace-nowrap">dia(s)</span>
                        </div>
                      </div>

                      {/* Bom */}
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">
                          Bom
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="90"
                            value={spacedRepetitionSettings.intervals.good}
                            onChange={(e) => updateInterval('good', parseInt(e.target.value) || 1)}
                            className="w-full px-2 py-1.5 text-sm border border-green-200 dark:border-green-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                          <span className="text-xs text-green-600 dark:text-green-400 whitespace-nowrap">dia(s)</span>
                        </div>
                      </div>

                      {/* Fácil */}
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                          Fácil
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="120"
                            value={spacedRepetitionSettings.intervals.easy}
                            onChange={(e) => updateInterval('easy', parseInt(e.target.value) || 1)}
                            className="w-full px-2 py-1.5 text-sm border border-blue-200 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <span className="text-xs text-blue-600 dark:text-blue-400 whitespace-nowrap">dia(s)</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Limit Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Quantos cards estudar?
                    </label>
                    
                    {/* Preset buttons */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {presetOptions.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setCardLimit(preset)}
                          className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                            cardLimit === preset
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>
                    
                    {/* Custom input */}
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Ou digite um número personalizado:
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={cardLimit}
                        onChange={(e) => setCardLimit(parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-sm"
                      />
                    </div>

                    {/* Info */}
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        💡 <strong>Dica:</strong> Sessões menores (10-20 cards) são mais eficazes para retenção. 
                        Você pode sempre fazer outra sessão depois!
                      </p>
                    </div>
                  </div>

                  {/* Advanced Settings Toggle */}
                  <div className="mb-3">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1"
                    >
                      {showAdvanced ? '▼' : '▶'} Configurações Avançadas
                    </button>
                  </div>

                  {/* Advanced: Multipliers */}
                  {showAdvanced && (
                    <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <h4 className="text-xs font-medium text-gray-900 dark:text-white mb-2">
                        Multiplicadores (para revisões subsequentes)
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Após a primeira revisão, o intervalo é multiplicado por estes valores.
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Difícil
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            step="0.1"
                            value={spacedRepetitionSettings.multipliers.hard}
                            onChange={(e) => updateMultiplier('hard', parseFloat(e.target.value) || 1)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Bom
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            step="0.1"
                            value={spacedRepetitionSettings.multipliers.good}
                            onChange={(e) => updateMultiplier('good', parseFloat(e.target.value) || 1)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Fácil
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            step="0.1"
                            value={spacedRepetitionSettings.multipliers.easy}
                            onChange={(e) => updateMultiplier('easy', parseFloat(e.target.value) || 1)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Disabled Mode Info */}
              {!spacedRepetitionSettings.enabled && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="flex gap-2">
                    <InformationCircleIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-xs font-medium text-yellow-900 dark:text-yellow-200">
                        Modo Simples
                      </h5>
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
                        Todos os cards estarão disponíveis para estudo, sem agendamento automático.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleResetSpacedRepetition}
                disabled={isSaving}
                className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50 order-3 sm:order-1"
              >
                Restaurar Padrão
              </button>
              <div className="flex flex-col sm:flex-row gap-3 order-1 sm:order-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSaving}
                  className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveConfiguration}
                  disabled={isSaving}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2.5 sm:py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all shadow-lg hover:shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Salvar esta configuração como padrão para este deck"
                >
                  <BookmarkIcon className="w-4 h-4 mr-2" />
                  {isSaving ? 'Salvando...' : 'Salvar Configuração'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default StudyConfigModal;